/* eslint-disable */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Map from '../homePage/Map';
import DistanceFromYouLabel from '../../components/DistanceFromYouLabel';
import { cityFromStationAddress, findUkraineRegionName, stationAddressLabel } from '../../utils/ukraineRegions';
import { useManyPortStatuses } from '../../hooks/useManyPortStatuses';
import { getCookie } from '../../hookServices/cookiesManager';
import { getClientId } from '../../hookServices/clientId';
import { HOST, PORT } from '../../environment';
import { resolveStartManyPorts } from '../startPage/openStartManyForStation';
import { stationDistanceKmFromUser } from '../../utils/stationDistanceKm';
import { stationsToKm220MapPorts } from './startManyMapPorts';
import StartManyShareButton from './StartManyShareButton';

async function fetchStationByNumber(portNumber) {
  const position = getCookie('userLocation');
  const clientId = getClientId();
  const response = await fetch(
    `${HOST}:${PORT}/api/device/v2/station?station_number=${portNumber}&lat=${position?.latitude || ''}&lon=${position?.longitude || ''}&clientUiId=${clientId || ''}`
  );
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  const data = await response.json();
  return { ...data, portNumber, number: data.number ?? portNumber };
}

function formatTimeAgo(dateString, t) {
  if (!dateString) return '';
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) {
    return `${diffSecs} ${t('seconds')}`;
  }
  if (diffMins < 60) {
    return `${diffMins} ${t('minutes')}`;
  }
  if (diffHours < 24) {
    return `${diffHours} ${t('hours')}`;
  }
  const lang = localStorage.getItem('i18nextLng') || 'ua';
  let daysText = t('days');
  if (lang === 'ua') {
    if (diffDays === 1) {
      daysText = 'день';
    } else if (diffDays >= 2 && diffDays <= 4) {
      daysText = 'дні';
    } else {
      daysText = 'днів';
    }
  }
  return `${diffDays} ${daysText}`;
}

/**
 * Shared location map + region meta for StartMany and StartPage.
 * @param {{ stations: object[], showShare?: boolean, showOnlineStatus?: boolean, showPrintOrder?: boolean, showMap?: boolean, shareAtEnd?: boolean, onSharePorts?: (ports: string[]) => void }} props
 */
export default function StartManyLocationHeader({
  stations,
  showShare = true,
  showOnlineStatus = true,
  showPrintOrder = false,
  showMap = true,
  shareAtEnd = false,
  onSharePorts,
}) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'ua').split('-')[0];
  const onSharePortsRef = useRef(onSharePorts);
  onSharePortsRef.current = onSharePorts;
  const [distanceKm, setDistanceKm] = useState(null);
  const [deviceStations, setDeviceStations] = useState(null);
  // Meta line uses ellipsis; tap region to expand and reveal online/offline period.
  const [metaExpanded, setMetaExpanded] = useState(false);

  // On StartPage (single station) expand to all ports on the same device for map + share.
  useEffect(() => {
    let active = true;
    if (!stations?.length || stations.length > 1) {
      setDeviceStations(null);
      return undefined;
    }
    const seed = stations[0];
    const seedNumber = seed?.portNumber ?? seed?.number;
    resolveStartManyPorts(seed, seedNumber)
      .then(async ports => {
        if (!active || !ports?.length) {
          return;
        }
        if (ports.length === 1 && String(ports[0]) === String(seedNumber)) {
          setDeviceStations(null);
          return;
        }
        const loaded = await Promise.all(
          ports.map(async portNumber => {
            if (String(portNumber) === String(seedNumber)) {
              return { ...seed, portNumber, number: seed.number ?? portNumber };
            }
            try {
              return await fetchStationByNumber(portNumber);
            } catch {
              return null;
            }
          })
        );
        if (active) {
          setDeviceStations(loaded.filter(Boolean));
        }
      })
      .catch(() => {
        if (active) {
          setDeviceStations(null);
        }
      });
    return () => {
      active = false;
    };
  }, [stations]);

  const effectiveStations = deviceStations?.length ? deviceStations : stations;

  const anchor = useMemo(() => {
    if (!effectiveStations?.length) {
      return null;
    }
    return (
      effectiveStations.find(s => !s.isError && s.latitude != null && s.longitude != null) ||
      effectiveStations.find(s => !s.isError) ||
      effectiveStations[0]
    );
  }, [effectiveStations]);

  const mapPorts = useMemo(() => stationsToKm220MapPorts(effectiveStations), [effectiveStations]);
  const portNumbers = useMemo(() => mapPorts.map(port => port.number).filter(Boolean), [mapPorts]);
  const sharePorts = useMemo(
    () => (effectiveStations || []).map(station => station.portNumber ?? station.number).filter(Boolean),
    [effectiveStations]
  );

  useEffect(() => {
    onSharePortsRef.current?.(sharePorts);
  }, [sharePorts]);
  const embedPortStatuses = useManyPortStatuses(portNumbers);
  const focusStationNumber = stations?.length === 1 ? (stations[0]?.portNumber ?? stations[0]?.number ?? null) : null;

  useEffect(() => {
    if (!showMap || !mapPorts.length) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 120);
    return () => window.clearTimeout(timer);
  }, [mapPorts, showMap]);

  const headerDistanceKm = showMap ? distanceKm : stationDistanceKmFromUser(anchor);

  const regionLabel = useMemo(() => {
    if (!anchor) {
      return t('startManyRegionUnknown');
    }
    const stored = stationAddressLabel(anchor.address);
    if (stored) {
      return stored;
    }
    const city = cityFromStationAddress(anchor.address);
    const region = findUkraineRegionName(anchor.latitude, anchor.longitude, lang);
    if (city && region && !region.toLowerCase().includes(city.toLowerCase())) {
      return `${city}, ${region}`;
    }
    return city || region || t('startManyRegionUnknown');
  }, [anchor, lang, t]);

  const updatedOn = anchor?.onlineUpdatedOn || anchor?.online_updated_on;
  const onlineStatusText =
    showOnlineStatus && updatedOn
      ? `${anchor?.online ? t('online') : t('offline')} ${formatTimeAgo(updatedOn, t)}`
      : null;

  if (!anchor || mapPorts.length === 0) {
    return null;
  }

  return (
    <header className="start-many-location-header">
      {showMap ? (
        <div className="start-many-location-header__map-wrap">
          <Map
            embedMode
            embedExtraTopPad={48}
            embedHeight="100%"
            km220Ports={mapPorts}
            embedPortStatuses={embedPortStatuses}
            focusStationNumber={focusStationNumber}
            tokaPorts={[]}
            ugvPorts={[]}
            evuaPorts={[]}
            isLoading={false}
            onEmbedDistanceKm={setDistanceKm}
          />
        </div>
      ) : null}
      <div className="start-many-location-header__meta">
        <p
          className={`start-many-location-header__meta-line${
            metaExpanded ? ' start-many-location-header__meta-line--expanded' : ''
          }`}
          aria-live="polite"
        >
          <button
            type="button"
            className="start-many-location-header__region"
            onClick={() => setMetaExpanded(open => !open)}
            aria-expanded={metaExpanded}
            aria-controls={onlineStatusText ? 'start-many-online-status' : undefined}
            title={onlineStatusText ? t('startManyRegionTapForStatus', { status: onlineStatusText }) : undefined}
          >
            {regionLabel}
          </button>
          {headerDistanceKm != null ? (
            <>
              <span className="start-many-location-header__dot" aria-hidden="true">
                {' '}
                •{' '}
              </span>
              <DistanceFromYouLabel km={headerDistanceKm} className="start-many-location-header__distance" />
            </>
          ) : null}
          {onlineStatusText && metaExpanded ? (
            <>
              <span className="start-many-location-header__dot" aria-hidden="true">
                {' '}
                •{' '}
              </span>
              <span id="start-many-online-status" className="start-many-location-header__status start-online-status">
                {onlineStatusText}
              </span>
            </>
          ) : null}
          {onlineStatusText && !metaExpanded ? (
            <span className="start-many-location-header__dot" aria-hidden="true">
              {' '}
              • …
            </span>
          ) : null}
        </p>
        {showShare && !shareAtEnd ? (
          <StartManyShareButton
            ports={sharePorts}
            showPrintOrder={showPrintOrder}
            printStationNumber={showPrintOrder ? (stations?.[0]?.portNumber ?? stations?.[0]?.number) : undefined}
            className="layout-page-chrome-btn start-many-location-header__share"
          />
        ) : null}
      </div>
    </header>
  );
}
